import { useHistory } from 'react-router-dom';
import { Paths, ProjectPathParams, createPath } from '../../../../app/router/app.router';
import { transformConnectionEventsForChart } from '../utils/kiosk.helpers';
import { AreaChart } from '../../../../ui/components/pure/charts/area/area-chart.component';
import {
  Paper,
  Grid,
  Typography,
  Button,
  Box,
  Container,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  formatTimestamp,
  formatKioskStatus,
  isKioskActive,
  calculateOfflineDuration,
  calculateEventDuration
} from '../utils/kiosk.helpers';
import { Chip } from '@mui/material';
import { Colors } from '../../../../ui/theme/colors';
import { RestError } from '@azure/core-http';
import { ErrorFullPage } from '../../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { LoadingIndicator } from '../../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { WindowTitle } from '../../../../core/window-title/window-title.component';
import { useQueryKioskConnectionHistory } from '../kiosk-status.queries';
import { KioskStateEvent } from '../types/kiosk.types';
import { useMemo } from 'react';
import { useKioskProjectMapping } from '../hooks/use-kiosk-project-mapping.hook';

interface KioskDetailViewProps {
  params: ProjectPathParams<Paths.kioskConnectionStatusDetail>;
}

function KioskDetailViewContent({ params }: KioskDetailViewProps) {
  const history = useHistory();
  const { data, isLoading, error } = useQueryKioskConnectionHistory(params.kioskID);

  const { getProjectNameForKiosk, getHubNameForKiosk } = useKioskProjectMapping(params.projectID);

  // Get the project name and hub name for this kiosk
  const projectName = useMemo(() => {
    if (!data) return 'Unknown project';
    return getProjectNameForKiosk(data.id);
  }, [data, getProjectNameForKiosk]);

  const hubName = useMemo(() => {
    if (!data) return 'Unknown hub';
    return getHubNameForKiosk(data.id);
  }, [data, getHubNameForKiosk]);

  const events = useMemo(() => (data?.events as unknown as KioskStateEvent[]) || [], [data?.events]);

  // Sort events by start time (most recent first)
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  }, [events]);

  if (isLoading) {
    return (
      <Container>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <LoadingIndicator />
        </Box>
      </Container>
    );
  }

  if (error) {
    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }
    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }
    return <ErrorFullPage />;
  }

  if (!data) {
    return <ErrorFullPage preset="NOT_FOUND" />;
  }

  const isActive = isKioskActive(data.connectionState);
  const statusLabel = formatKioskStatus(data.connectionState);
  const chartData = transformConnectionEventsForChart(events);

  const handleBack = () => {
    history.push(createPath(Paths.kioskConnectionStatusReport, { projectID: params.projectID }));
  };

  return (
    <Container>
      <Grid container spacing={3}>
        {/* Header with back button */}
        <Grid size={12}>
          <Button startIcon={<ArrowBack />} onClick={handleBack} style={{ marginBottom: '16px', color: 'white' }}>
            Back to Kiosk Status
          </Button>
        </Grid>

        {/* Kiosk Metadata */}
        <Grid size={12}>
          <Paper style={{ padding: '24px' }}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <Typography variant="h6">Kiosk Details</Typography>
                <Box
                  sx={{
                    mt: 2
                  }}
                >
                  <Divider style={{ margin: '0 -24px' }} />
                </Box>
              </Grid>

              {/* Details section */}
              <Grid container spacing={2} style={{ marginTop: '8px' }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Project Name
                  </Typography>
                  <Typography variant="body1">{projectName}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Hub Name
                  </Typography>
                  <Typography variant="body1">{hubName}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Kiosk ID
                  </Typography>
                  <Typography variant="body1">{data.id}</Typography>
                </Grid>
              </Grid>

              {/* Software section */}
              <Grid container spacing={2} style={{ marginTop: '16px' }}>
                <Grid style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }} size={12}>
                  <Typography variant="h6">Software</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Version
                  </Typography>
                  <Typography variant="body1">{data.softwareVersion || '-'}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Operating System
                  </Typography>
                  <Typography variant="body1">{data.osName || '-'}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Operating System Version
                  </Typography>
                  <Typography variant="body1">{data.osVersion || '-'}</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Operating System Architecture
                  </Typography>
                  <Typography variant="body1">{data.osArchitecture || '-'}</Typography>
                </Grid>
              </Grid>

              {/* Status section */}
              <Grid container spacing={2} style={{ marginTop: '16px' }}>
                <Grid style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }} size={12}>
                  <Typography variant="h6">Status</Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Box
                    sx={{
                      mt: 1
                    }}
                  >
                    <Chip
                      label={statusLabel}
                      size="small"
                      style={{
                        backgroundColor: isActive ? Colors.collectedGreen : Colors.cancelledDeletedRed,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">{formatTimestamp(data.lastUpdated)}</Typography>
                </Grid>
                {!isActive && data.offlineStartTime && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Offline Since
                    </Typography>
                    <Typography variant="body1">{formatTimestamp(data.offlineStartTime)}</Typography>
                  </Grid>
                )}
                {!isActive && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 4
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Offline Duration
                    </Typography>
                    <Typography variant="body1">{calculateOfflineDuration(data.lastUpdated)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Connection History Chart */}
        <Grid size={12}>
          <Paper style={{ padding: '24px' }}>
            <Typography variant="h6" gutterBottom>
              Connection History
            </Typography>
            {chartData.datasets.length > 0 && chartData.labels.length > 0 ? (
              <AreaChart
                isLoading={false}
                data={{
                  labels: chartData.labels,
                  datasets: chartData.datasets
                }}
                dateLabelUnit={chartData.dateLabelUnit}
                maxDate={chartData.labels[chartData.labels.length - 1]}
                xAxisTickSource="auto"
                yAxisLabels={[0, 1]}
                yAxisMax={1}
                yAxisLabelCallback={(tickValue) => {
                  if (tickValue === 1) {
                    return 'Connected';
                  }
                  if (tickValue === 0) {
                    return 'Disconnected';
                  }
                  return '';
                }}
                tooltipFilterCallback={(tooltipItem, index, tooltipItems) => {
                  // Filter out duplicate tooltips at the same x-coordinate
                  // Keep only the first tooltip item for each x position
                  const currentX = tooltipItem.parsed.x;
                  const firstIndex = tooltipItems.findIndex((item) => item.parsed.x === currentX);
                  return index === firstIndex;
                }}
                tooltipLabelCallback={(tooltipItem) => {
                  const value = tooltipItem.parsed.y;
                  // Return empty string for dataset label, and show only the status
                  return value === 1 ? 'Connected' : 'Disconnected';
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px'
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No connection history available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Events Table */}
        <Grid size={12}>
          <Paper style={{ padding: '24px' }}>
            <Typography variant="h6" gutterBottom>
              Connection Events
            </Typography>
            {sortedEvents.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        Start Time
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        Status
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        End Time
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        Duration
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEvents.map((event, index) => {
                    const eventIsActive = isKioskActive(event.connectionState);
                    const eventStatusLabel = formatKioskStatus(event.connectionState);
                    return (
                      <TableRow key={index}>
                        <TableCell>{formatTimestamp(event.startTime)}</TableCell>
                        <TableCell>
                          <Chip
                            label={eventStatusLabel}
                            size="small"
                            style={{
                              backgroundColor: eventIsActive ? Colors.collectedGreen : Colors.cancelledDeletedRed,
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell>{event.endTime ? formatTimestamp(event.endTime) : '-'}</TableCell>
                        <TableCell>{calculateEventDuration(event)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px'
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  No connection events available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export function KioskDetailView({ params }: KioskDetailViewProps) {
  return (
    <Container>
      <PermissionBoundary
        availableUiFeatures={['internal-groundfloor']}
        notify={{
          render() {
            return <ErrorFullPage preset="ACCESS_DENIED" />;
          }
        }}
      >
        <WindowTitle pageTitle="Reports" detail="Kiosk Status Details" />
        <KioskDetailViewContent params={params} />
      </PermissionBoundary>
    </Container>
  );
}
